import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Button,
  Container,
  Divider,
  Flex,
  Heading,
  HStack,
  IconButton,
  Image,
  SimpleGrid,
  Stack,
  Tag,
  Text,
  useColorModeValue,
} from "@chakra-ui/react";
import { Link as RouterLink, useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import { ChevronLeftIcon, ChevronRightIcon } from "@chakra-ui/icons";

const newPhotos = [
  "IMG_6097.jpg", "IMG_5642.jpg", "IMG_5322.jpg", "IMG_5650.jpg", "IMG_6117.jpg",
  "IMG_6056.jpg", "IMG_6316.jpg", "IMG_6315.jpg", "IMG_5210.jpg", "IMG_5611.jpg",
  "IMG_5710.jpg", "IMG_5222.jpg", "IMG_6104.jpg", "IMG_6088.jpg", "IMG_5823.jpg",
  "IMG_5582.jpg", "IMG_5698.jpg", "IMG_6259.jpg", "IMG_6109.jpg",
].map((filename, index) => ({
  src: `/traversal-media/${filename}`,
  alt: `Photo du laboratoire TRAVERSAL ${index + 1}`,
}));

const labVideos = [
  {
    src: "/traversal-media/DRAFT%201%20-%20Hackathon.mp4",
    alt: "Hackathon",
    poster: "/traversal_logo.png",
  },
  {
    src: "/traversal-media/DRAFT%201%20-%20SCIVC%20showcase.mp4",
    alt: "Présentation du SCVIC",
    poster: "/traversal_logo.png",
  },
];

function VideoSlide({ video }) {
  const videoRef = useRef(null);
  const [started, setStarted] = useState(false);
  const [playError, setPlayError] = useState(false);

  const play = async () => {
    setStarted(true);
    setPlayError(false);
    try {
      await videoRef.current.play();
      videoRef.current?.focus();
    } catch {
      setPlayError(true);
    }
  };

  return (
    <Box position="relative" w="100%" h="100%" bg="black">
      <Box
        as="video"
        ref={videoRef}
        src={video.src}
        poster={video.poster}
        aria-label={video.alt}
        controls={started}
        controlsList="nodownload"
        playsInline
        preload="none"
        onEnded={() => setStarted(false)}
        onContextMenu={(event) => event.preventDefault()}
        w="100%"
        h="100%"
        objectFit="contain"
      >
        Votre navigateur ne prend pas en charge la lecture vidéo intégrée.
      </Box>
      {!started && (
        <Box
          as="button"
          type="button"
          aria-label={`Lire ${video.alt}`}
          position="absolute"
          inset={0}
          w="100%"
          h="100%"
          onClick={play}
          cursor="pointer"
          data-group=""
          _focusVisible={{ outline: "3px solid #06B6D4", outlineOffset: "-3px" }}
        >
          <Image src={video.poster} alt="" w="100%" h="100%" objectFit="contain" />
          <Flex
            position="absolute"
            top="50%"
            left="50%"
            transform="translate(-50%, -50%)"
            align="center"
            justify="center"
            boxSize={{ base: "64px", md: "88px" }}
            borderRadius="full"
            bg="#06B6D4"
            border="3px solid white"
            boxShadow="0 8px 30px rgba(0,0,0,0.4)"
            _groupHover={{ bg: "#0891B2" }}
          >
            <Box as="svg" viewBox="0 0 24 24" boxSize="36px" fill="white" aria-hidden="true">
              <path d="M8 5v14l11-7z" />
            </Box>
          </Flex>
        </Box>
      )}
      {playError && (
        <Text role="status" position="absolute" top={2} left={2} right={2} bg="blackAlpha.800" color="white" p={2}>
          La lecture n'a pas pu démarrer. Veuillez réessayer.
        </Text>
      )}
    </Box>
  );
}

/* --------------------------
   Small reusable components
-------------------------- */

function Section({ title, subtitle, children }) {
  const titleColor = useColorModeValue("#0F172A", "white");
  const subColor = useColorModeValue("gray.600", "gray.300");
  const divider = useColorModeValue("rgba(2,6,23,0.08)", "rgba(148,163,184,0.18)");

  return (
    <Box py={{ base: 4, md: 6 }}>
      <Container maxW="1200px">
        <Stack spacing={2} mb={{ base: 6, md: 8 }}>
          <Heading fontSize={{ base: "2xl", md: "3xl" }} color={titleColor} letterSpacing="tight">
            {title}
          </Heading>
          {subtitle ? (
            <Text fontSize={{ base: "md", md: "lg" }} color={subColor} maxW="900px">
              {subtitle}
            </Text>
          ) : null}
        </Stack>

        {children}

        <Divider mt={{ base: 10, md: 14 }} borderColor={divider} />
      </Container>
    </Box>
  );
}

function InfoCard({ label, text }) {
  const cardBg = useColorModeValue("white", "rgba(255,255,255,0.04)");
  const border = useColorModeValue("rgba(2,6,23,0.10)", "rgba(148,163,184,0.18)");
  const bodyColor = useColorModeValue("gray.700", "gray.200");

  return (
    <Box
      bg={cardBg}
      border="1px solid"
      borderColor={border}
      borderRadius="22px"
      p={{ base: 6, md: 7 }}
      boxShadow={useColorModeValue("0 12px 30px rgba(0,0,0,0.08)", "none")}
    >
      <HStack spacing={3} mb={3}>
        <Tag
          size="lg"
          borderRadius="full"
          bg={useColorModeValue("rgba(6,182,212,0.10)", "rgba(6,182,212,0.18)")}
          color={useColorModeValue("#0E7490", "#67E8F9")}
          fontWeight="900"
          letterSpacing="0.06em"
        >
          {label}
        </Tag>
      </HStack>

      <Text fontSize={{ base: "md", md: "lg" }} color={bodyColor} textAlign="left" lineHeight={1.75}>
        {text}
      </Text>
    </Box>
  );
}

function StatPill({ k, v }) {
  const border = useColorModeValue("rgba(2,6,23,0.10)", "rgba(148,163,184,0.18)");
  const bg = useColorModeValue("rgba(255,255,255,0.70)", "rgba(255,255,255,0.04)");
  const kColor = useColorModeValue("gray.600", "gray.300");
  const vColor = useColorModeValue("#0F172A", "white");

  return (
    <Box border="1px solid" borderColor={border} bg={bg} borderRadius="18px" p={4}>
      <Text fontSize="xs" letterSpacing="0.12em" fontWeight="900" color={kColor}>
        {k.toUpperCase()}
      </Text>
      <Text fontSize="sm" fontWeight="800" color={vColor} mt={1}>
        {v}
      </Text>
    </Box>
  );
}

/* --------------------------
   Inline Carousel (no imports)
   - works for desktop and mobile
   - no cropping: objectFit="contain"
-------------------------- */
function InlineCarousel({
  images,
  title,
  subtitle,
  autoPlay = true,
  isVideo = false,
  intervalMs = 4500,
  aspect = { base: "16 / 10", md: "16 / 8" },
}) {
  const [idx, setIdx] = useState(0);

  const border = useColorModeValue("rgba(2,6,23,0.10)", "rgba(148,163,184,0.18)");
  const panelBg = useColorModeValue("white", "rgba(255,255,255,0.03)");
  const titleColor = useColorModeValue("#0F172A", "white");
  const subColor = useColorModeValue("gray.600", "gray.300");
  const dotOff = useColorModeValue("rgba(2,6,23,0.18)", "rgba(255,255,255,0.22)");
  const dotOn = "#06B6D4";
  const shadow = useColorModeValue("0 14px 40px rgba(0,0,0,0.10)", "none");
  const mediaBg = useColorModeValue("rgba(2,6,23,0.02)", "rgba(255,255,255,0.03)");
  const arrowBg = useColorModeValue("rgba(255,255,255,0.70)", "rgba(15,23,42,0.55)");

  const n = images?.length || 0;

  useEffect(() => {
    if (isVideo || !autoPlay || n <= 1) return;
    const t = setInterval(() => setIdx((v) => (v + 1) % n), intervalMs);
    return () => clearInterval(t);
  }, [autoPlay, intervalMs, n, isVideo]);

  const prev = () => setIdx((v) => (v - 1 + n) % n);
  const next = () => setIdx((v) => (v + 1) % n);

  if (!n) return null;

  const current = images[idx];

  return (
    <Box
      border="1px solid"
      borderColor={border}
      borderRadius="26px"
      overflow="hidden"
      bg={panelBg}
      boxShadow={shadow}
      role="region"
      aria-label={title}
      aria-roledescription="carousel"
    >
      <Box p={{ base: 4, md: 6 }}>
        <Heading fontSize={{ base: "lg", md: "xl" }} color={titleColor} letterSpacing="tight">
          {title}
        </Heading>
        {subtitle ? (
          <Text color={subColor} mt={1}>
            {subtitle}
          </Text>
        ) : null}
      </Box>

      <Divider borderColor={border} />

      <Box position="relative" p={{ base: 3, md: 4 }}>
        <Box
          border="1px solid"
          borderColor={border}
          borderRadius="22px"
          bg={mediaBg}
          overflow="hidden"
        >
          <Box
            w="100%"
            sx={{
              aspectRatio: aspect, // modern browsers; keeps frame stable
            }}
          >
            <Flex w="100%" h="100%" align="center" justify="center" p={{ base: 3, md: 5 }}>
              {isVideo ? (
                <VideoSlide key={current.src} video={current} />
              ) : <Image
                src={typeof current === "string" ? current : current.src}
                alt={typeof current === "string" ? `slide-${idx + 1}` : current.alt || `slide-${idx + 1}`}
                w="100%"
                h="100%"
                objectFit="contain" // ✅ no cropping
                draggable={false}
                loading="lazy"
                decoding="async"
              />}
            </Flex>
          </Box>
        </Box>

        {/* Arrows */}
        {n > 1 && (
          <>
            <IconButton
              aria-label={`Diapositive précédente dans ${title}`}
              icon={<ChevronLeftIcon />}
              position="absolute"
              left={{ base: 2, md: 4 }}
              top="50%"
              transform="translateY(-50%)"
              onClick={prev}
              variant="ghost"
              bg={arrowBg}
              _hover={{ filter: "brightness(0.97)" }}
              borderRadius="999px"
            />
            <IconButton
              aria-label={`Diapositive suivante dans ${title}`}
              icon={<ChevronRightIcon />}
              position="absolute"
              right={{ base: 2, md: 4 }}
              top="50%"
              transform="translateY(-50%)"
              onClick={next}
              variant="ghost"
              bg={arrowBg}
              _hover={{ filter: "brightness(0.97)" }}
              borderRadius="999px"
            />
          </>
        )}

        {/* Dots */}
        {n > 1 && (
          <HStack justify="center" spacing={2} pt={4}>
            {images.map((_, i) => (
              <Box
                as="button"
                type="button"
                key={i}
                aria-label={`Aller à la diapositive ${i + 1} dans ${title}`}
                aria-current={i === idx ? "true" : undefined}
                w="8px"
                h="8px"
                borderRadius="999px"
                bg={i === idx ? dotOn : dotOff}
                cursor="pointer"
                onClick={() => setIdx(i)}
              />
            ))}
          </HStack>
        )}
        {isVideo && (
          <Text textAlign="center" color={subColor} mt={3} aria-live="polite">
            {current.alt} ({idx + 1} / {n})
          </Text>
        )}
      </Box>
    </Box>
  );
}

/* --------------------------
   Home Page
-------------------------- */
export default function HomePage() {
  const isMobile = useSelector((state) => state.windowSize.isMobile);
  const location = useLocation();

  // EN/FR base prefix
  const lang = location.pathname.startsWith("/fr") ? "fr" : "en";
  const base = lang === "fr" ? "/fr" : "/en";

  // Palette aligned to your navbar/footer
  const accent = "#06B6D4";
  const accent2 = "#22D3EE";
  const titleColor = useColorModeValue("#0F172A", "white");
  const subColor = useColorModeValue("gray.600", "gray.300");
  const softBorder = useColorModeValue("rgba(2,6,23,0.10)", "rgba(148,163,184,0.18)");
  const pageBg = useColorModeValue("white", "#0B1220");
  const panelBg = useColorModeValue("white", "rgba(255,255,255,0.03)");

  const heroBg = useColorModeValue(
    "radial-gradient(1000px 450px at 15% 10%, rgba(6,182,212,0.18), transparent 60%), radial-gradient(900px 450px at 85% 30%, rgba(34,211,238,0.14), transparent 55%), linear-gradient(180deg, rgba(15,23,42,0.02), rgba(15,23,42,0.00))",
    "radial-gradient(1000px 450px at 15% 10%, rgba(6,182,212,0.20), transparent 60%), radial-gradient(900px 450px at 85% 30%, rgba(34,211,238,0.14), transparent 55%), linear-gradient(180deg, rgba(255,255,255,0.03), rgba(255,255,255,0.00))"
  );

  // ✅ Put your real images here (public folder recommended)
  // Example: public/home/highlight_1.png  -> "home/highlight_1.png"
  const highlights = useMemo(
    () => [
      ...newPhotos.slice(0, 2),
      { src: "/LabImage5.png", alt: "TRAVERSAL highlight 1" },
      { src: "/LabImage2.png", alt: "TRAVERSAL highlight 2" },
      { src: "/LabImage3.png", alt: "TRAVERSAL highlight 3" },
    ],
    []
  );

  const labPictures = useMemo(
    () => [
      ...newPhotos.slice(2, 5),
      { src: "/LabImage4.png", alt: "Lab 1" },
      { src: "/lab_picture.jpg", alt: "Lab 2" },
      { src: "/LabImage2.png", alt: "Lab 3" },
    ],
    []
  );

  return (
    <Box bg={pageBg}>
      {/* HERO */}
      <Box
        pt={{ base: 8, md: 12 }}
        pb={{ base: 10, md: 14 }}
        bg={heroBg}
        borderBottom="1px solid"
        borderColor={softBorder}
      >
        <Container maxW="1200px">
          <SimpleGrid columns={{ base: 1, md: 2 }} spacing={{ base: 8, md: 12 }} alignItems="center">
            <Stack spacing={5}>
              <HStack spacing={2} flexWrap="wrap">
                <Tag
                  size="lg"
                  borderRadius="full"
                  bg={useColorModeValue("white", "rgba(255,255,255,0.06)")}
                  border="1px solid"
                  borderColor={softBorder}
                  fontWeight="800"
                >
                  University of Ottawa
                </Tag>

                <Tag
                  size="lg"
                  borderRadius="full"
                  bg={useColorModeValue("rgba(6,182,212,0.12)", "rgba(6,182,212,0.18)")}
                  color={useColorModeValue("#0E7490", "#67E8F9")}
                  fontWeight="900"
                >
                  TRAVERSAL
                </Tag>
              </HStack>

              <Heading
                color={titleColor}
                fontSize={{ base: "2xl", md: "3xl", lg: "4xl" }}
                lineHeight={1.05}
                letterSpacing="tight"
              >
                TRAVERSAL:{" "}
                <Box as="span" bgClip="text" bgGradient={`linear(to-r, ${accent}, ${accent2})`}>
                  Recherche fiable sur la mobilité autonome
                </Box>
              </Heading>

              <Text fontSize={{ base: "md", md: "lg" }} color={subColor} maxW="62ch" lineHeight={1.8}>
                Nous développons une IA fiable, une connectivité résiliente et une intelligence embarquée pour permettre des transports autonomes plus sûrs, en faisant le lien entre la recherche, les bancs d'essai et le déploiement dans le monde réel.
              </Text>

              <HStack spacing={3} flexWrap="wrap">
                <Button
                  as={RouterLink}
                  to={`/publications-fr`}
                  bg={accent}
                  color="#001018"
                  fontWeight="900"
                  size="lg"
                  borderRadius="16px"
                  _hover={{ filter: "brightness(0.95)" }}
                >
                  Explorer les publications
                </Button>

                <Button
                  as={RouterLink}
                  to={`/seminars-fr`}
                  variant="outline"
                  borderColor={softBorder}
                  size="lg"
                  borderRadius="16px"
                  fontWeight="900"
                >
                  Séminaires
                </Button>

                <Button
                  as={RouterLink}
                  to={`/contact-us-fr`}
                  variant="ghost"
                  size="lg"
                  borderRadius="16px"
                  fontWeight="900"
                  color={titleColor}
                  _hover={{ bg: useColorModeValue("rgba(2,6,23,0.05)", "rgba(255,255,255,0.06)") }}
                >
                  Collaborer →
                </Button>
              </HStack>

              <SimpleGrid columns={{ base: 1, sm: 3 }} spacing={3} pt={2}>
                <StatPill k="Focus" v="Trustworthy autonomy" />
                <StatPill k="Methods" v="AI • Networking • Edge" />
                <StatPill k="Impact" v="Safety • Reliability" />
              </SimpleGrid>
            </Stack>

            {/* Right visual: no cropping */}
            <Box
              borderRadius="26px"
              overflow="hidden"
              border="1px solid"
              borderColor={softBorder}
              bg={panelBg}
              boxShadow={useColorModeValue("0 18px 50px rgba(0,0,0,0.12)", "none")}
            >


              <Box p={{ base: 3, md: 4 }}>
                <Image src="lab_image1.png" alt="TRAVERSAL" w="100%" h="auto" objectFit="contain" />
              </Box>
            </Box>
          </SimpleGrid>
        </Container>
      </Box>

      {/* HIGHLIGHTS (inline carousel) */}
      <Box py={{ base: 10, md: 12 }}>
        <Container maxW="1200px">
          <InlineCarousel
            images={highlights}
            title="Points saillants"
            subtitle="Projets, partenariats et travaux expérimentaux du laboratoire."
            autoPlay={!isMobile} // optional: disable autoplay on mobile
            intervalMs={4500}
            aspect={{ base: "16 / 10", md: "16 / 8" }}
          />
        </Container>
      </Box>

      {/* MISSION / VISION / GOAL */}
      <Section title="Mission, Vision & Objectif">
        <SimpleGrid columns={{ base: 1, md: 3 }} spacing={6}>
          <InfoCard
            label="Mission"
            text={
              <>
                CREATE TRAVERSAL s'engage à doter les HQP (Hautes Qualifications Professionnelles) de compétences techniques et comportementales, à promouvoir la collaboration interdisciplinaire et à propulser le Canada au premier plan du leadership en matière de technologie autonome grâce aux véhicules autonomes (VA) et aux véhicules aériens sans équipage (UAV).
              </>
            }
          />
          <InfoCard
            label="Vision"
            text={
              <>
                CREATE TRAVERSAL imagine le Canada à l'avant-garde de l'innovation dans les technologies autonomes fiables, sûres, sécurisées, intelligentes et responsables.
              </>
            }
          />
          <InfoCard
            label="Objectif"
            text={
              <>
                Renforcer la main-d'œuvre et le pipeline de talents du secteur des véhicules connectés et autonomes (VCA) au Canada grâce à des modules de formation stratégiquement conçus et des stages en entreprise, en collaboration entre le milieu universitaire et l'industrie, afin de pallier les pénuries de compétences.
              </>
            }
          />
        </SimpleGrid>
      </Section>

      <Box py={{ base: 2, md: 4 }}>
        <Container maxW="1200px">
          <InlineCarousel
            images={labVideos}
            title="Vidéos du laboratoire"
            subtitle="Visionnez notre hackathon et la présentation du SCVIC."
            isVideo
            autoPlay={false}
            aspect={{ base: "16 / 9", md: "16 / 9" }}
          />
        </Container>
      </Box>

      {/* WHAT WE DO */}
      <Section title="Ce qu'on fait" subtitle="Thèmes de recherche fondamentaux qui définissent l'orientation du projet.">
        <SimpleGrid columns={{ base: 1, md: 2, lg: 4 }} spacing={5}>
          {[
            {
              t: "IA digne de confiance",
              d: "Perception robuste, prédiction et aide à la décision en contexte d’incertitude."
            },
            {
              t: "Intelligence en périphérie",
              d: "Inférence à faible latence, compression sémantique et déploiement infonuagique en périphérie."
            },
            {
              t: "Connectivité résiliente",
              d: "V2X, orchestration des réseaux et communications axées sur la fiabilité."
            },
            {
              t: "Sécurité et conformité",
              d: "Estimation des risques, respect des contraintes et comportements autonomes alignés sur les politiques."
            }
          ].map((x) => (
            <Box
              key={x.t}
              border="1px solid"
              borderColor={softBorder}
              borderRadius="22px"
              p={6}
              bg={panelBg}
              boxShadow={useColorModeValue("0 10px 24px rgba(0,0,0,0.07)", "none")}
            >
              <Text fontWeight="900" color={titleColor} fontSize="lg" mb={2}>
                {x.t}
              </Text>
              <Text color={subColor} fontSize="md" lineHeight={1.75}>
                {x.d}
              </Text>
            </Box>
          ))}
        </SimpleGrid>
      </Section>

      {/* LAB PICTURES (second inline carousel) */}
      <Box pb={{ base: 14, md: 16 }}>
        <Container maxW="1200px">
          <InlineCarousel
            images={labPictures}
            title="Photos du laboratoire"
            subtitle="Vidéos de démonstration, bancs d'essai et mises à jour de la recherche."
            autoPlay={!isMobile}
            intervalMs={5000}
            aspect={{ base: "16 / 10", md: "16 / 8" }}
          />
        </Container>
      </Box>

      <Box pb={{ base: 14, md: 16 }}>
        <Container maxW="1200px">
          <Stack spacing={{ base: 8, md: 12 }}>
            <InlineCarousel
              images={newPhotos.slice(5, 12)}
              title="Galerie du laboratoire 1"
              subtitle="Activités, événements et collaborations de TRAVERSAL."
              autoPlay={!isMobile}
              intervalMs={5000}
            />
            <InlineCarousel
              images={newPhotos.slice(12)}
              title="Galerie du laboratoire 2"
              subtitle="D'autres moments de la communauté TRAVERSAL."
              autoPlay={!isMobile}
              intervalMs={5000}
            />
          </Stack>
        </Container>
      </Box>

      {/* FINAL CTA */}
      <Box
        py={{ base: 12, md: 16 }}
        bg={useColorModeValue("rgba(2,6,23,0.03)", "rgba(255,255,255,0.03)")}
        borderTop="1px solid"
        borderColor={softBorder}
      >
        <Container maxW="1200px">
          <Box
            border="1px solid"
            borderColor={softBorder}
            bg={panelBg}
            borderRadius="26px"
            p={{ base: 7, md: 10 }}
            boxShadow={useColorModeValue("0 16px 44px rgba(0,0,0,0.10)", "none")}
          >
            <SimpleGrid columns={{ base: 1, md: 2 }} spacing={{ base: 6, md: 10 }} alignItems="center">
              <Stack spacing={3}>
                <Heading color={titleColor} fontSize={{ base: "xl", md: "2xl" }}>
                  Intéressé(e) par une collaboration ?
                </Heading>
                <Text color={subColor} fontSize={{ base: "md", md: "lg" }} lineHeight={1.75}>
                  Nous sommes ouverts aux partenariats entre les milieux universitaires, l'industrie et le secteur public – contactez-nous pour explorer les possibilités de recherche conjointe, de jeux de données, de bancs d'essai et de transfert de technologie.
                </Text>
              </Stack>

              <Flex justify={{ base: "flex-start", md: "flex-end" }} gap={3} flexWrap="wrap">
                <Button
                  as={RouterLink}
                  to={`/contact-us-fr`}
                  bg={accent}
                  color="#001018"
                  fontWeight="900"
                  size="lg"
                  borderRadius="16px"
                  _hover={{ filter: "brightness(0.95)" }}
                >
                  Contactez-Nous
                </Button>
                <Button
                  as={RouterLink}
                  to={`/about-us-fr`}
                  variant="outline"
                  borderColor={softBorder}
                  size="lg"
                  borderRadius="16px"
                  fontWeight="900"
                >
                  Découvrez L'équipe
                </Button>
              </Flex>
            </SimpleGrid>
          </Box>
        </Container>
      </Box>
    </Box>
  );
}
